// NPM packages
import React, { useState } from 'react'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { Tooltip } from '@mui/material'

// Components
import { DetailedUser } from '@matech/thebigpos-sdk'
import {
	TextField,
	Form,
	FormRow,
} from '../../../../components/formControls'
import {
	IconButton,
	LoadingBtn as LoadingButton,
} from '../../../../components/Button'

// Services
import useForm from '../../../../hooks/useForm'
import { ErrorListProps } from '../../../../components/ErrorList'
import useUser from '../../../../hooks/useUser'
import AlertBox from '../../../../components/AlertBox'

export type UserProfileFormData = {
	firstName?: string
	lastName?: string
	title?: string
	emailEnabled?: boolean
	textEnabled?: boolean
	pushEnabled?: boolean
}

export type UserProfileFormProps = {
	user: DetailedUser
	onSubmit: (data: UserProfileFormData) => Promise<void>
	apiErrors?: ErrorListProps['errors']
}

const UserProfileForm = ({
	user,
	onSubmit,
	apiErrors,
}: UserProfileFormProps) => {
	const { isAdmin, isEffectiveSuperAdmin } = useUser()
	const { formatMessage } = useIntl()
	const [isUnlocked, setIsUnlocked] = useState(false)

	const messages = isEffectiveSuperAdmin
		? formatMessage({ id: 'user_profile.email_change' })
		: formatMessage({ id: 'warnings.change_email_denied' })

	const schema = yup
		.object()
		.shape({
			firstName: yup.string().required(),
			lastName: yup.string().required(),
			title: yup.string(),
			email: yup.string().email().required(),
			emailEnabled: yup.boolean(),
			textEnabled: yup.boolean(),
			pushEnabled: yup.boolean(),
		})
		.required()

	const {
		control,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm({
		schema,
		defaultValues: {
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			title: user?.title || '',
			email: user?.email || '',
			emailEnabled: user?.notificationSettings?.emailEnabled || false,
			textEnabled: user?.notificationSettings?.textEnabled || false,
			pushEnabled: user?.notificationSettings?.pushEnabled || false,
		},
	})

	const handleFormSubmit = async (data: UserProfileFormData) => {
		await onSubmit(data)
	}

	const handleUnlockEmailClick = () => {
		if (isEffectiveSuperAdmin) {
			setIsUnlocked(true)
		}
	}

	return (
		<Form
			onSubmit={handleSubmit(handleFormSubmit)}
			errors={apiErrors}
		>
			<FormRow>
				<TextField
					name="firstName"
					label="First Name"
					fullWidth
					control={control}
					errors={errors}
					required
				/>
			</FormRow>
			<FormRow>
				<TextField
					name="lastName"
					label="Last Name"
					fullWidth
					control={control}
					errors={errors}
					required
				/>
			</FormRow>
			<FormRow>
				<TextField
					name="title"
					label="Title"
					fullWidth
					control={control}
					errors={errors}
					disabled={!isAdmin}
				/>
			</FormRow>
			<FormRow className="relative">
				<TextField
					name="email"
					label={formatMessage({ id: 'global.email' })}
					fullWidth
					control={control}
					errors={errors}
					required
					disabled={!isUnlocked}
					style={{ width: '95%' }}
				/>
				<Tooltip title={messages}>
					<IconButton
						onClick={handleUnlockEmailClick}
						style={{
							position: 'absolute',
							top: 20,
							right: -5,
							padding: 0,
							margin: '2px',
							zIndex: 999,
						}}
						size="small"
					>
						{isUnlocked ? <LockOpenIcon /> : <LockIcon />}
					</IconButton>
				</Tooltip>
				{isUnlocked && (
					<AlertBox severity="warning" className="mt-4">
						{formatMessage({ id: 'warnings.change_email' })}
					</AlertBox>
				)}
			</FormRow>
			<LoadingButton
				type="submit"
				text="Save"
				loading={isSubmitting}
				disabled={isSubmitting}
			/>
		</Form>
	)
}

export default UserProfileForm
